import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChatCircleIcon } from '@phosphor-icons/react';
import { formatTimeIndex, resolveMediaTrimHandleSeconds } from '@/shared/functions';

/** Keep the floating time label a couple px inside the track edges. */
const TIME_LABEL_EDGE_PAD_PX = 2;

/** One frame at 30fps — used for arrow-key nudging of trim handles. */
const FRAME_STEP_SECONDS = 1 / 30;

interface TrackProps {
  currentTime: number;
  duration: number;
  handleTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTimelineClick: (e: React.MouseEvent<HTMLDivElement>, startTime?: number, updateMediaContext?: (field: string, value: any) => void, mediaItem?: any) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleMouseLeave: () => void;
  hoverTime: number | null;
  filteredTranscript: any[];
  activeSegmentIndex: number;
  handleSegmentClick: (startTime: number) => void;
  showTranscriptMarkers: boolean;
  startTime?: number;
  endTime?: number;
  updateMediaContext?: (field: string, value: any) => void;
  mediaItem?: any;
  isPlaying: boolean;
  mediaRef?: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
  setCurrentTime?: (time: number) => void;
  isEditingMediaContext?: boolean;
  /** True while a trim handle drag is in progress — parent can freeze player layout. */
  onTrimDragActiveChange?: (active: boolean) => void;
  /** Capture mode: ignore seeks so a drag across the scrubber cannot change the frame. */
  disabled?: boolean;
}

interface IndicatorProps {
  type: 'start' | 'end';
  position: number;
  duration: number;
  isEditing?: boolean;
  isDragging: 'start' | 'end' | null;
  onMouseDown: (e: React.MouseEvent, type: 'start' | 'end') => void;
}

const TimeIndicator: React.FC<IndicatorProps> = ({
  type,
  position,
  duration,
  isEditing,
  isDragging,
  onMouseDown
}) => {
  const isActive = isDragging === type;
  
  return (
    <div 
      className={`absolute box-border appearance-none ${
        type === 'start' ? 'bg-green-600' : 'bg-red-600'
      } ${
        isEditing 
          ? 'w-[10px] h-[17px] rounded-full top-[-5px]' 
          : 'w-[5px] h-1.5 top-0.5 border-1 border-gray-600 border-solid'
      }`}
      style={{ 
        left: `calc(${(position / duration) * 100}%)`,
        transform: 'translateX(-50%)',
        zIndex: isActive ? 35 : 30,
        cursor: isEditing ? 'grab' : 'default',
        pointerEvents: isEditing ? 'auto' : 'none',
        border: isActive ? '2px solid white' : '1px solid transparent',
        boxShadow: isActive ? '0 0 5px rgba(255, 255, 255, 0.5)' : 'none'
      }}
      onMouseDown={(e) => onMouseDown(e, type)}
      onClick={(e) => {
        // Prevent the track's onClick from seeking / handling the click after a handle press
        e.preventDefault();
        e.stopPropagation();
      }}
    />
  );
};

const Track: React.FC<TrackProps> = ({
  currentTime,
  duration,
  handleTimeChange,
  handleTimelineClick,
  handleMouseMove,
  handleMouseLeave,
  hoverTime,
  filteredTranscript,
  activeSegmentIndex,
  handleSegmentClick,
  showTranscriptMarkers,
  startTime,
  endTime,
  updateMediaContext,
  mediaItem,
  isPlaying,
  mediaRef,
  setCurrentTime,
  isEditingMediaContext,
  onTrimDragActiveChange,
  disabled = false,
}) => {
  const [showTranscriptMarkersState, setShowTranscriptMarkersState] = useState(showTranscriptMarkers);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<'start' | 'end' | null>(null);
  /** Which trim handle was most recently dragged — arrow keys nudge this one. */
  const [startOrEnd, setStartOrEnd] = useState<'start' | 'end' | null>(null);
  /**
   * Local trim times while a handle is dragged. Committing on every mousemove
   * re-rendered the event-modal tree and re-fitted the video (visible jitter).
   * Keep drag ephemeral here; persist via ``updateMediaContext`` on mouseup.
   */
  const [localStartTime, setLocalStartTime] = useState<number | null>(null);
  const [localEndTime, setLocalEndTime] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef<'start' | 'end' | null>(null);
  const localStartRef = useRef<number | null>(null);
  const localEndRef = useRef<number | null>(null);
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const setCurrentTimeRef = useRef(setCurrentTime);
  setCurrentTimeRef.current = setCurrentTime;
  const mediaRefProp = useRef(mediaRef);
  mediaRefProp.current = mediaRef;
  const onTrimDragActiveChangeRef = useRef(onTrimDragActiveChange);
  onTrimDragActiveChangeRef.current = onTrimDragActiveChange;

  // Prefer player-parsed numeric props; fall back to mediaContext (incl. legacy
  // clock strings) so handle positions stay correct for older saved trims.
  const committedStart = resolveMediaTrimHandleSeconds(
    startTime ?? mediaItem?.mediaContext?.startTime,
    0,
  );
  const committedEnd = resolveMediaTrimHandleSeconds(
    endTime ?? mediaItem?.mediaContext?.endTime,
    duration,
  );
  const displayStart = localStartTime ?? committedStart;
  const displayEnd = localEndTime ?? committedEnd;

  useEffect(() => {
    setShowTranscriptMarkersState(showTranscriptMarkers);
  }, [showTranscriptMarkers]);

  useEffect(() => {
    isDraggingRef.current = isDragging;
    onTrimDragActiveChangeRef.current?.(!!isDragging);
  }, [isDragging]);

  // Clear the parent layout freeze if Track unmounts mid-drag (media switch,
  // modal close, viewer teardown) so resize/fit updates are not stuck off.
  useEffect(() => {
    return () => {
      isDraggingRef.current = null;
      onTrimDragActiveChangeRef.current?.(false);
    };
  }, []);

  // Drop ephemeral drag locals whenever a drag ends. Waiting for parent
  // ``Object.is(committed, local)`` stuck forever when the parent never echoed
  // that exact value (noop updateMediaContext, duration-fallback drift on the
  // undragged end, or a later nudge changing committed while local still held).
  useEffect(() => {
    if (isDragging) return;
    setLocalStartTime(null);
    setLocalEndTime(null);
    localStartRef.current = null;
    localEndRef.current = null;
  }, [isDragging]);

  useEffect(() => {
    if (!isEditingMediaContext) {
      setSelectedIndicator(null);
      setStartOrEnd(null);
      setLocalStartTime(null);
      setLocalEndTime(null);
      localStartRef.current = null;
      localEndRef.current = null;
    }
  }, [isEditingMediaContext]);

  const previewTime = (newTime: number) => {
    // Prefer the player seek helper (also syncs play/pause UI in edit mode).
    // Use refs so window drag listeners always seek with the latest helpers.
    if (setCurrentTimeRef.current) {
      setCurrentTimeRef.current(newTime);
      return;
    }
    if (mediaRefProp.current?.current) {
      mediaRefProp.current.current.currentTime = newTime;
    }
  };

  /**
   * Nudge the last-dragged trim handle by one frame and show that frame in the player.
   * No-op until the user has dragged a handle (``startOrEnd`` set), and when already
   * at the clamp boundary (avoids update storms while holding an arrow key).
   */
  const nudgeActiveHandle = (direction: -1 | 1) => {
    if (!updateMediaContext || !isEditingMediaContext || !startOrEnd) return;

    const startBound = displayStart;
    const endBound = displayEnd;
    const handleTime = startOrEnd === 'start' ? startBound : endBound;
    let newTime = handleTime + direction * FRAME_STEP_SECONDS;

    if (startOrEnd === 'start') {
      newTime = Math.max(0, Math.min(newTime, Math.max(0, endBound - FRAME_STEP_SECONDS)));
    } else {
      newTime = Math.min(duration, Math.max(newTime, Math.min(duration, startBound + FRAME_STEP_SECONDS)));
    }
    newTime = Math.round(newTime * 1000) / 1000;

    if (newTime === handleTime) return;

    updateMediaContext(startOrEnd === 'start' ? 'startTime' : 'endTime', newTime);
    previewTime(newTime);
  };

  // Keep a stable keydown callback so the window listener is not torn down/re-added
  // on every startTime change (holding an arrow was re-entrant and could loop updates).
  const nudgeActiveHandleRef = useRef(nudgeActiveHandle);
  nudgeActiveHandleRef.current = nudgeActiveHandle;

  // Bounds + preview must read refs — window mousemove keeps a stale render closure
  // while ``isDragging`` stays truthy across many local time updates.
  const applyLocalTrim = (type: 'start' | 'end', t: number) => {
    const mediaDuration = durationRef.current;
    const startBound = localStartRef.current ?? 0;
    const endBound = localEndRef.current ?? mediaDuration;
    if (type === 'start') {
      const maxStart = Math.max(0, endBound - FRAME_STEP_SECONDS);
      if (t >= endBound) return;
      const next = Math.max(0, Math.min(t, maxStart));
      localStartRef.current = next;
      setLocalStartTime(next);
      previewTime(next);
      return;
    }
    const minEnd = Math.min(mediaDuration, startBound + FRAME_STEP_SECONDS);
    if (t <= startBound) return;
    const next = Math.min(mediaDuration, Math.max(t, minEnd));
    localEndRef.current = next;
    setLocalEndTime(next);
    previewTime(next);
  };

  const handleDragStart = (e: React.MouseEvent, type: 'start' | 'end') => {
    if (!isEditingMediaContext) return;
    e.preventDefault();
    e.stopPropagation();

    // Seed local times from committed values so the undragged handle stays put.
    localStartRef.current = committedStart;
    localEndRef.current = committedEnd;
    setLocalStartTime(committedStart);
    setLocalEndTime(committedEnd);

    // Remember which handle is active so arrow keys can nudge it after the drag
    setStartOrEnd(type);
    setSelectedIndicator(type);
    setIsDragging(type);
    
    const rect = e.currentTarget.closest('.track-container')?.getBoundingClientRect();
    if (!rect) {
      previewTime(type === 'start' ? committedStart : committedEnd);
      return;
    }

    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = Math.round(percentage * duration * 1000) / 1000;
    applyLocalTrim(type, newTime);
  };

  const handleScrubStart = (e: React.MouseEvent) => {
    if (isEditingMediaContext) return;
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position relative to the timeline container, not the scrubber dot
    const timelineElement = e.currentTarget.closest('.track-container');
    if (!timelineElement) return;
    
    const rect = timelineElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    // Set scrub time first to prevent position flash
    setScrubTime(newTime);
    setIsScrubbing(true);
    
    if (setCurrentTime) {
      setCurrentTime(newTime);
    }
  };

  const handleScrubMove = (e: MouseEvent) => {
    if (!isScrubbing) return;

    // Instance ref — not document.querySelector (wrong track when modal + fullscreen both mount).
    const timelineElement = trackRef.current;
    if (!timelineElement) return;

    const rect = timelineElement.getBoundingClientRect();
    // Clamp the x position to the track bounds
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    // Always update scrub time and current time
    setScrubTime(newTime);
    
    if (setCurrentTime) {
      setCurrentTime(newTime);
    }
  };

  const handleScrubEnd = () => {
    // Ensure the final position is saved before ending the scrub
    if (isScrubbing && setCurrentTime) {
      // Use the last known scrub time, or fall back to current time
      const finalTime = scrubTime !== null ? scrubTime : currentTime;
      setCurrentTime(finalTime);
    }
    
    setIsScrubbing(false);
    setScrubTime(null);
  };

  // After a trim handle is dragged, left/right arrows step that handle frame-by-frame.
  // Use capture so we receive the event before the focused track's React handler can
  // stopPropagation (drag-end focuses the track, which previously swallowed arrows).
  useEffect(() => {
    if (!isEditingMediaContext || !startOrEnd) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target instanceof HTMLElement &&
          (e.target.isContentEditable || e.target.closest('.mdxeditor-root-contenteditable')))
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      nudgeActiveHandleRef.current(e.key === 'ArrowLeft' ? -1 : 1);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isEditingMediaContext, startOrEnd]);

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newTime = (percentage / 100) * duration;

    if (isEditingMediaContext && selectedIndicator && updateMediaContext) {
      const t = Math.round(newTime * 1000) / 1000;
      if (selectedIndicator === 'start' && t < displayEnd) {
        updateMediaContext('startTime', t);
        previewTime(t);
      } else if (selectedIndicator === 'end' && t > displayStart) {
        updateMediaContext('endTime', t);
        previewTime(t);
      }
      return;
    }

    handleTimelineClick(e, startTime, updateMediaContext, mediaItem);
  };

  const handleDragMove = (e: MouseEvent) => {
    const type = isDraggingRef.current;
    if (!type || !isEditingMediaContext) return;

    // Instance ref — not document.querySelector (wrong track when modal + fullscreen both mount).
    const timelineElement = trackRef.current;
    if (!timelineElement) return;

    const rect = timelineElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const dragDuration = durationRef.current || duration;
    const t = Math.round(percentage * dragDuration * 1000) / 1000;
    setStartOrEnd(type);
    applyLocalTrim(type, t);
  };

  const handleDragEnd = () => {
    const type = isDraggingRef.current;
    if (type && updateMediaContext) {
      if (type === 'start' && localStartRef.current !== null) {
        updateMediaContext('startTime', localStartRef.current);
      } else if (type === 'end' && localEndRef.current !== null) {
        updateMediaContext('endTime', localEndRef.current);
      }
    }
    isDraggingRef.current = null;
    // Clear locals in the same event as the commit so handle position / arrow
    // nudges follow committed props immediately (React 18 batches the parent
    // updateMediaContext setState with these). Do not wait for an Object.is echo.
    setLocalStartTime(null);
    setLocalEndTime(null);
    localStartRef.current = null;
    localEndRef.current = null;
    setIsDragging(null);
    // Keep keyboard focus on the track so arrow keys remain available after a drag
    trackRef.current?.focus({ preventScroll: true });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Arrows are handled in the window capture listener. preventDefault here as a
    // fallback so the page doesn't scroll if that listener isn't mounted yet.
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
    // handleDragMove/End close over refs — rebind only when a drag starts/stops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  useEffect(() => {
    if (isScrubbing) {
      window.addEventListener('mousemove', handleScrubMove);
      window.addEventListener('mouseup', handleScrubEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleScrubMove);
      window.removeEventListener('mouseup', handleScrubEnd);
    };
  }, [isScrubbing]);

  // Use scrubTime for position when scrubbing, otherwise use currentTime
  const displayTime = isScrubbing && scrubTime !== null ? scrubTime : currentTime;

  // While editing after a handle drag, show that handle's time (incl. fractions) on the track
  const activeHandleTime =
    isEditingMediaContext && startOrEnd
      ? startOrEnd === 'start'
        ? displayStart
        : displayEnd
      : null;
  const timeLabelSeconds =
    activeHandleTime !== null
      ? activeHandleTime
      : hoverTime !== null && hoverTime >= 0 && hoverTime <= duration
        ? hoverTime
        : null;
  const timeLabelText =
    timeLabelSeconds !== null
      ? formatTimeIndex(timeLabelSeconds.toString(), isEditingMediaContext ? 3 : 0)
      : '';
  const timeLabelRef = useRef<HTMLDivElement>(null);
  /** Pixel center of the label within the track; clamped so text never clips. */
  const [timeLabelCenterPx, setTimeLabelCenterPx] = useState<number | null>(null);

  // Follow the handle while centered; pin at the track edges once the label would overflow.
  useLayoutEffect(() => {
    if (timeLabelSeconds === null || duration <= 0) {
      setTimeLabelCenterPx(null);
      return;
    }
    const trackEl = trackRef.current;
    const labelEl = timeLabelRef.current;
    if (!trackEl || !labelEl) return;

    const trackWidth = trackEl.clientWidth;
    const labelWidth = labelEl.offsetWidth;
    if (trackWidth <= 0 || labelWidth <= 0) return;

    const idealCenter = (timeLabelSeconds / duration) * trackWidth;
    const half = labelWidth / 2;
    const minCenter = half + TIME_LABEL_EDGE_PAD_PX;
    const maxCenter = Math.max(minCenter, trackWidth - half - TIME_LABEL_EDGE_PAD_PX);
    const next = Math.max(minCenter, Math.min(maxCenter, idealCenter));
    setTimeLabelCenterPx((prev) => (prev !== null && Math.abs(prev - next) < 0.5 ? prev : next));
  }, [timeLabelSeconds, duration, timeLabelText, isEditingMediaContext]);

  return (
    <div 
      ref={trackRef}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      data-testid="media-timeline"
      aria-label="Media timeline"
      aria-valuemin={0}
      aria-valuemax={duration}
      aria-valuenow={displayTime}
      aria-valuetext={`${Math.floor(displayTime / 60)}:${String(Math.floor(displayTime % 60)).padStart(2, '0')}`}
      onKeyDown={disabled ? undefined : onKeyDown} 
      className={`track-container relative box-border w-auto min-w-0 self-stretch h-1.5 min-h-1.5 z-40 mt-5 mr-3.5 mb-6 ml-3 focus:outline focus:outline-2 focus:outline-white rounded ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`} 
      aria-disabled={disabled || undefined}
      onMouseMove={disabled ? undefined : handleMouseMove} 
      onMouseLeave={disabled ? undefined : handleMouseLeave} 
      onClick={disabled ? undefined : handleTrackClick}
    >

      {/* Timeline Background */}
      <div className="absolute box-border top-0.5 left-0.5 h-full bg-gray-600 rounded-full z-0" style={{ width: '100%' }}></div>

      {/* Current Time Indicator */}
      {(!isEditingMediaContext || isPlaying) && (
        <>
          {/* Progress Track */}
          <div className="absolute box-border top-0.5 left-0.5 h-full bg-gradient-to-r from-white/50 via-white/70 to-white/80 rounded-l-full z-10 border-b-1 border-gray-800 border-solid" style={{ 
            width: `calc(${(displayTime / duration) * 100}%)`,
            display: startTime !== undefined || endTime !== undefined || isEditingMediaContext ? 'none' : 'block'
          }} />
          {/* Current Time Indicator Dot */}
          <div 
            className="absolute top-[-4px] right-[-4px] w-[17px] h-[17px] appearance-none cursor-grab bg-gradient-to-br from-white to-gray-500 rounded-full border-r-1 border-b-1 border-gray-800 border-solid" 
            style={{ 
              left: `calc(${(displayTime / duration) * 100}%)`,
              zIndex: isEditingMediaContext ? 15 : 40
            }}
            onMouseDown={handleScrubStart}
          />
        </>
      )}

      {/* Start and End Time Range */}
      {(isEditingMediaContext || (startTime !== undefined && endTime !== undefined)) && duration > 0 && (
        <>
          {/* Light gray track between indicators */}
          <div className="absolute box-border top-0.5 h-full bg-gray-400" style={{ 
            left: `calc(${(displayStart / duration) * 100}%)`,
            width: `calc(${((displayEnd - displayStart) / duration) * 100}%)`,
            zIndex: 5
          }} />
          
          <TimeIndicator
            type="start"
            position={displayStart}
            duration={duration}
            isEditing={isEditingMediaContext}
            isDragging={isDragging}
            onMouseDown={handleDragStart}
          />

          <TimeIndicator
            type="end"
            position={displayEnd}
            duration={duration}
            isEditing={isEditingMediaContext}
            isDragging={isDragging}
            onMouseDown={handleDragStart}
          />
        </>
      )}

      {/* Timeline Input */}
      <input type="range" value={currentTime} min={0} max={duration || 0} step="0.01" onChange={handleTimeChange} disabled={disabled} className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent rounded-full outline-none z-20" />

      {/* Hover / active-handle time indicator — follows the handle, then pins at edges. */}
      {timeLabelSeconds !== null && duration > 0 && (
          <div
            ref={timeLabelRef}
            className={`absolute flex items-center justify-center text-white bg-gray-900/20 px-1 py-[0.5px] rounded border-1 border-white/20 border-solid whitespace-nowrap ${isEditingMediaContext ? 'text-[14px]' : 'text-[10px]'}`}
            style={{
              top: isEditingMediaContext ? '-20px' : '14px',
              // Fallback % center until measured; then pixel-clamped so edges don't clip.
              left: timeLabelCenterPx !== null
                ? `${timeLabelCenterPx}px`
                : `${(timeLabelSeconds / duration) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <span className="cursor-pointer tabular-nums">
              {timeLabelText}
            </span>
          </div>
      )}

      {/* Transcript Markers */}
      {showTranscriptMarkersState && !isEditingMediaContext && filteredTranscript.map((item, index) => (
        <div
          key={index}
          data-testid="transcript-marker"
          className={`box-border absolute h-[8px] w-0.5 cursor-pointer mx-1 mt-[-11px] ${index === activeSegmentIndex ? 'bg-white text-white' : (item.start < currentTime ? 'bg-white/50 text-white/65 hover:bg-white hover:text-white' : 'bg-yellow-400/60 text-yellow-400/60 hover:bg-white hover:text-white')}`}
          style={{
            left: `${(item.start / duration) * 100}%`,
            transform: 'translateX(-50%) translateY(50%)',
            zIndex: 5,
          }}
          onClick={() => {
            if (!disabled) handleSegmentClick(item.start);
          }}
        >
          <ChatCircleIcon weight="fill" size={12} className="relative top-[-14px] left-[-5px]" />
        </div>
      ))}
    </div>
  );
};

export default Track;
